import CopyToClipboardView from "@/components/views/CopyToClipboardView";
import { formatAddress, formatAggregatedName, formatLocaleDate, formatPhoneNumber } from "@/utils/formatUtils";
import { cssVariables } from "@/utils/utils";
import { Descriptions, Drawer } from "antd";
import { useTranslation } from "react-i18next";
import { SecondaryAgentRepresentative } from "../../types";

type Props = {
  representative?: SecondaryAgentRepresentative;
  onCancel: () => void;
};

export const SecondaryAgentRepresentativeDrawer = ({ representative, onCancel }: Props) => {
  const { t } = useTranslation();

  return (
    <Drawer size={520} title={t("agent.titles.representativeData")} open={!!representative} onClose={onCancel}>
      {representative ? (
        <Descriptions
          styles={{ label: { fontWeight: "bold" } }}
          column={1}
          bordered
          items={[
            {
              label: t("agent.attrs.aggregatedName"),
              children: formatAggregatedName(representative),
            },
            {
              label: t("agent.titles.personalData"),
              children: (
                <Descriptions
                  column={1}
                  size="small"
                  layout="vertical"
                  bordered
                  styles={{
                    label: { paddingTop: 8, color: cssVariables.colorText },
                    content: { paddingTop: 8 },
                  }}
                  items={[
                    {
                      label: t("agent.attrs.personalIdentificationNumberLong"),
                      children: <CopyToClipboardView content={representative.personalIdentificationNumber} />,
                    },
                    {
                      label: t("agent.attrs.birthDate"),
                      children: formatLocaleDate(representative.birthDate),
                    },
                    {
                      label: t("agent.attrs.identityCardNumber"),
                      children: representative.identityCardNumber ? (
                        <CopyToClipboardView content={representative.identityCardNumber} />
                      ) : (
                        <span className="sub-header-info">{t("common.noData")}</span>
                      ),
                    },
                    {
                      label: t("common.enums.educationLevel._label"),
                      children: representative.educationLevel ? (
                        t(`common.enums.educationLevel.${representative.educationLevel}`)
                      ) : (
                        <span className="sub-header-info">{t("common.noData")}</span>
                      ),
                    },
                  ]}
                />
              ),
            },
            {
              label: t("agent.titles.basicData"),
              children: (
                <Descriptions
                  size="small"
                  column={1}
                  layout="vertical"
                  bordered
                  styles={{ label: { paddingTop: 8, color: cssVariables.colorText }, content: { paddingTop: 8 } }}
                  items={[
                    {
                      label: t("agent.attrs.email"),
                      children: (
                        <CopyToClipboardView
                          content={representative.email}
                          wrapper={<a href={`mailto:${representative.email}`}>{representative.email}</a>}
                        />
                      ),
                    },
                    {
                      label: t("agent.attrs.phone"),
                      children: (
                        <CopyToClipboardView
                          content={representative.phone}
                          wrapper={
                            <a href={`tel:${representative.phone}`}>{formatPhoneNumber(representative.phone)}</a>
                          }
                        />
                      ),
                    },
                  ]}
                />
              ),
            },
            {
              label: t("agent.attrs.address"),
              children: formatAddress(representative.address),
            },
          ]}
        />
      ) : undefined}
    </Drawer>
  );
};
